import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import HeroCarousel, { type FocusItem } from "@/pages/home/components/HeroCarousel";
import QuickLinks from "@/pages/home/components/QuickLinks";
import LatestNews from "@/pages/home/components/LatestNews";
import SiteIntro from "@/pages/home/components/SiteIntro";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CharacterCard from "@/components/feature/CharacterCard";
import { allCharacters, allEpisodes } from "@/utils/content";

const focusItems: FocusItem[] = [
  {
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20anime%20cel%20shaded%20cinematic%20scene%20of%20two%20ninja%20silhouettes%20clashing%20beside%20a%20huge%20waterfall%20between%20ancient%20stone%20statues%2C%20swirling%20orange%20and%20dark%20blue%20energy%2C%20dramatic%20dusk%20sky%2C%20epic%20emotional%20mood%2C%20high%20detail%2C%20vibrant%20contrasting%20colors&width=1280&height=720&seq=home-focus-01&orientation=landscape",
    tag: "经典剧情",
    title: "终结之谷的对决",
    desc: "鸣人与佐助的宿命一战，是整部作品最令人难忘的羁绊转折点。",
    to: "/episode/ep-010",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20anime%20cel%20shaded%20heroic%20portrait%20of%20a%20young%20blond%20ninja%20standing%20before%20a%20village%20at%20sunrise%2C%20warm%20orange%20light%2C%20flowing%20energy%20aura%2C%20determined%20expression%2C%20cinematic%20composition%2C%20high%20detail%2C%20vibrant%20saturated%20colors&width=1280&height=720&seq=home-focus-02&orientation=landscape",
    tag: "热点角色",
    title: "漩涡鸣人：从吊车尾到火影",
    desc: "永不服输的少年，用行动证明了认可与羁绊才是真正的力量。",
    to: "/character/naruto",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20anime%20cel%20shaded%20solemn%20scene%20of%20a%20cloaked%20ninja%20standing%20alone%20under%20a%20crimson%20moon%20with%20crows%20flying%2C%20deep%20red%20and%20black%20tones%2C%20melancholic%20cinematic%20mood%2C%20high%20detail%2C%20emotional%20atmosphere&width=1280&height=720&seq=home-focus-03&orientation=landscape",
    tag: "焦点角色",
    title: "宇智波鼬：背负骂名的守护者",
    desc: "真相揭晓的那一刻，多少人重新理解了「英雄」的重量。",
    to: "/character/itachi",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20anime%20cel%20shaded%20festive%20scene%20of%20a%20ninja%20village%20at%20night%20with%20lanterns%20and%20banners%20celebrating%20an%20anniversary%2C%20warm%20golden%20lights%2C%20joyful%20atmosphere%2C%20cinematic%20composition%2C%20high%20detail%2C%20vibrant%20colors&width=1280&height=720&seq=home-focus-04&orientation=landscape",
    tag: "最新资讯",
    title: "动画开播周年纪念企划公开",
    desc: "官方推出全新视觉企划与限定周边，共同重温忍者世界的感动。",
    to: "/news/news-001",
  },
];

export default function Home() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const featuredEpisodes = allEpisodes.slice(0, 4);
  const featuredCharacters = allCharacters.slice(0, 4);

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 h-full w-full">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20abstract%20artistic%20background%20inspired%20by%20a%20ninja%20world%2C%20flowing%20orange%20and%20deep%20red%20ink%20swirls%20with%20subtle%20spiral%20motifs%2C%20soft%20dark%20charcoal%20vignette%2C%20elegant%20minimal%20texture%2C%20high%20contrast%20dark%20tones%20for%20readable%20text%20overlay%2C%20high%20detail&width=1920&height=900&seq=home-hero-bg&orientation=landscape"
            alt="火影忍者动漫频道主题背景"
            title="火影忍者动漫频道"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80" />

        <div className="relative w-full px-4 py-16 md:px-6 md:py-24">
          <div className="mx-auto max-w-7xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-fill" />
              火影忍者 · 动漫专题资料站
            </span>
            <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              火影忍者动漫频道
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-lg">
              完整的动漫剧情介绍、角色分析与最新资讯，陪你走进忍者的世界，
              重温那些关于羁绊、梦想与守护的故事。
            </p>

            <div className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-50/25 bg-background-50/15 px-4 py-3 backdrop-blur">
                <i className="ri-search-line text-background-200" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  placeholder="搜索剧集、角色或资讯"
                  className="w-full bg-transparent text-sm text-background-50 outline-none placeholder:text-background-300"
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                className="rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                立即搜索
              </button>
            </div>
            <p className="mt-3 text-xs text-background-300">
              热门搜索：鸣人 · 中忍考试 · 写轮眼 · 终结之谷
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-star-smile-line" />
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">焦点内容</h2>
            </div>
            <HeroCarousel items={focusItems} />
          </div>
          <div className="flex flex-col gap-6">
            <QuickLinks />
            <LatestNews />
          </div>
        </div>
      </div>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                精选剧集
              </h2>
              <p className="mt-2 text-sm text-foreground-600">从经典篇章开始，重温忍者世界的名场面。</p>
            </div>
            <Link
              to="/episodes"
              className="shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
            >
              全部剧集
              <i className="ri-arrow-right-line ml-1" />
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featuredEpisodes.map((episode) => (
              <EpisodeCard key={episode.id} episode={episode} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              人气角色
            </h2>
            <p className="mt-2 text-sm text-foreground-600">深入了解每一位忍者的背景、能力与羁绊。</p>
          </div>
          <Link
            to="/characters"
            className="shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
          >
            全部角色
            <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {featuredCharacters.map((character) => (
            <CharacterCard key={character.id} character={character} />
          ))}
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 pb-4 md:px-6">
        <SiteIntro />
      </div>
    </div>
  );
}